import React, { useEffect, useState, Fragment } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Row, Col, Card, Badge, Button } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import swal from "sweetalert";

const VendorView = () => {
 const { id } = useParams();
 const navigate = useNavigate();
 const [vendor, setVendor] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetchVendor();
 }, [id]);

 const fetchVendor = async () => {
 try {
 setLoading(true);
 const response = await api.get(`/vendors/${id}`);

 if (response.data.success) {
 setVendor(response.data.data);
 }
 } catch (error) {
 console.error("Error fetching vendor:", error);
 swal("Error", "Failed to load vendor details", "error");
 navigate("/vendors");
 } finally {
 setLoading(false);
 }
 };

 const getStatusBadge = (status) => {
 switch (status) {
 case "Active":
 return "success";
 case "Inactive":
 return "warning";
 case "Blacklisted":
 return "danger";
 default:
 return "secondary";
 }
 };

 if (loading) {
 return (
 <Fragment>
 <PageTitle activeMenu="View Vendor" motherMenu="Vendor Management" />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Body>
 <div className="text-center py-5">
 <div className="spinner-border text-primary" role="status">
 <span className="visually-hidden">Loading...</span>
 </div>
 <p className="mt-3">Loading vendor details...</p>
 </div>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
 }

 if (!vendor) {
 return (
 <Fragment>
 <PageTitle activeMenu="View Vendor" motherMenu="Vendor Management" />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Body>
 <div className="text-center py-5">
 <p className="text-muted">Vendor not found</p>
 <Button variant="primary" onClick={() => navigate("/vendors")}>
 Back to List
 </Button>
 </div>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
 }

 return (
 <Fragment>
 <PageTitle activeMenu="View Vendor" motherMenu="Vendor Management" />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Vendor Details</Card.Title>
 <div className="d-flex gap-2">
 <Button variant="primary" as={Link} to={`/vendors/edit/${vendor._id}`}>
 <i className="fas fa-edit me-2"></i>Edit
 </Button>
 <Button variant="secondary" onClick={() => navigate("/vendors")}>
 <i className="fas fa-arrow-left me-2"></i>Back to List
 </Button>
 </div>
 </Card.Header>

 <Card.Body>
 {/* BASIC INFORMATION */}
 <div className="mb-4">
 <h5 className="mb-3">Basic Information</h5>
 <Row>
 <Col md={6}>
 <div className="mb-3">
 <strong>Vendor Name:</strong>
 <p className="mb-0">{vendor.name}</p>
 </div>
 </Col>

 <Col md={6}>
 <div className="mb-3">
 <strong>Category:</strong>
 <p className="mb-0">{vendor.category?.name || "N/A"}</p>
 </div>
 </Col>
 </Row>

 <Row>
 <Col md={6}>
 <div className="mb-3">
 <strong>Contact Person:</strong>
 <p className="mb-0">{vendor.contactPerson}</p>
 </div>
 </Col>

 <Col md={6}>
 <div className="mb-3">
 <strong>Status:</strong>
 <p className="mb-0">
 <Badge bg={getStatusBadge(vendor.status)}>
 {vendor.status}
 </Badge>
 </p>
 </div>
 </Col>
 </Row>
 </div>

 <hr />

 {/* CONTACT INFORMATION */}
 <div className="mb-4">
 <h5 className="mb-3">Contact Information</h5>
 <Row>
 <Col md={6}>
 <div className="mb-3">
 <strong>Email:</strong>
 <p className="mb-0">
 <a href={`mailto:${vendor.email}`}>{vendor.email}</a>
 </p>
 </div>
 </Col>

 <Col md={6}>
 <div className="mb-3">
 <strong>Phone:</strong>
 <p className="mb-0">
 <a href={`tel:${vendor.phone}`}>{vendor.phone}</a>
 </p>
 </div>
 </Col>
 </Row>

 <Row>
 <Col md={12}>
 <div className="mb-3">
 <strong>Address:</strong>
 <p className="mb-0">{vendor.address}</p>
 </div>
 </Col>
 </Row>
 </div>

 <hr />

 {/* BUSINESS INFORMATION */}
 <div className="mb-4">
 <h5 className="mb-3">Business Information</h5>
 <Row>
 <Col md={6}>
 <div className="mb-3">
 <strong>GST / Tax Number:</strong>
 <p className="mb-0">{vendor.gst || "Not provided"}</p>
 </div>
 </Col>
 <Col md={6}>
 <div className="mb-3">
 <strong>PAN Number:</strong>
 <p className="mb-0">{vendor.panNumber || "Not provided"}</p>
 </div>
 </Col>
 </Row>

 <Row>
 <Col md={6}>
 <div className="mb-3">
 <strong>Aadhaar Number:</strong>
 <p className="mb-0">{vendor.aadhaarNumber || "Not provided"}</p>
 </div>
 </Col>
 </Row>

 <Row>
 <Col md={6}>
 <div className="mb-3">
 <strong>PAN Document:</strong>
 <p className="mb-0">
 {vendor.panDocument?.fileUrl ? (
 <a href={getFileUrl(vendor.panDocument.fileUrl)} target="_blank" rel="noreferrer">
 {vendor.panDocument.fileName || "Open PAN document"}
 </a>
 ) : (
 "Not uploaded"
 )}
 </p>
 </div>
 </Col>
 <Col md={6}>
 <div className="mb-3">
 <strong>Aadhaar Document:</strong>
 <p className="mb-0">
 {vendor.aadhaarDocument?.fileUrl ? (
 <a href={getFileUrl(vendor.aadhaarDocument.fileUrl)} target="_blank" rel="noreferrer">
 {vendor.aadhaarDocument.fileName || "Open Aadhaar document"}
 </a>
 ) : (
 "Not uploaded"
 )}
 </p>
 </div>
 </Col>
 </Row>

 {/* BANK INFORMATION */}
 {vendor.bank && (
 <>
 <Row>
 <Col md={6}>
 <div className="mb-3">
 <strong>Bank Name:</strong>
 <p className="mb-0">{vendor.bank.bankName || "N/A"}</p>
 </div>
 </Col>
 <Col md={6}>
 <div className="mb-3">
 <strong>Account Holder:</strong>
 <p className="mb-0">{vendor.bank.accountHolder || "N/A"}</p>
 </div>
 </Col>
 </Row>

 <Row>
 <Col md={6}>
 <div className="mb-3">
 <strong>Account No:</strong>
 <p className="mb-0">{vendor.bank.accountNo || "N/A"}</p>
 </div>
 </Col>

 <Col md={6}>
 <div className="mb-3">
 <strong>IFSC Code:</strong>
 <p className="mb-0">{vendor.bank.ifsc || "N/A"}</p>
 </div>
 </Col>
 </Row>
 </>
 )}
 </div>

 <hr />

 {/* ADDITIONAL INFORMATION */}
 <div className="mb-4">
 <h5 className="mb-3">Additional Information</h5>
 <Row>
 <Col md={6}>
 <div className="mb-3">
 <strong>Created Date:</strong>
 <p className="mb-0">
 {new Date(vendor.createdAt).toLocaleDateString("en-IN", {
 year: "numeric",
 month: "long",
 day: "numeric",
 hour: "2-digit",
 minute: "2-digit"
 })}
 </p>
 </div>
 </Col>

 <Col md={6}>
 <div className="mb-3">
 <strong>Last Updated:</strong>
 <p className="mb-0">
 {new Date(vendor.updatedAt).toLocaleDateString("en-IN", {
 year: "numeric",
 month: "long",
 day: "numeric",
 hour: "2-digit",
 minute: "2-digit"
 })}
 </p>
 </div>
 </Col>
 </Row>
 </div>

 {/* CATEGORY DESCRIPTION */}
 {vendor.category?.description && (
 <>
 <hr />
 <div className="mb-4">
 <h5 className="mb-3">Category Details</h5>
 <Row>
 <Col md={12}>
 <div className="mb-3">
 <strong>Description:</strong>
 <p className="mb-0">{vendor.category.description}</p>
 </div>
 </Col>
 </Row>
 </div>
 </>
 )}
 </Card.Body>

 <Card.Footer className="d-flex justify-content-end gap-2">
 <Button variant="primary" as={Link} to={`/vendors/edit/${vendor._id}`}>
 <i className="fas fa-edit me-2"></i>Edit Vendor
 </Button>
 <Button variant="secondary" onClick={() => navigate("/vendors")}>
 <i className="fas fa-arrow-left me-2"></i>Back to List
 </Button>
 </Card.Footer>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default VendorView;
